import React, { createContext, useContext, ReactNode } from 'react';
import { Bank, Warehouse, ProductCategory, Country, GekdCode, TnvedCode, Unit, BankAccountNew, ContractorNew, ContractNew, ProductNew } from '../types';
import { useCRUD } from '../hooks/useCRUD';
import {
  MOCK_BANKS,
  MOCK_CONTRACTORS,
  MOCK_CONTRACTS,
  MOCK_WAREHOUSES,
  MOCK_PRODUCT_CATEGORIES,
  MOCK_PRODUCTS,
  MOCK_COUNTRIES,
  MOCK_GEKD_CODES,
  MOCK_TNVED_CODES,
  MOCK_UNITS
} from '../mocks';

interface DataContextType {
  banks: ReturnType<typeof useCRUD<Bank>>;
  warehouses: ReturnType<typeof useCRUD<Warehouse>>;
  productCategories: ReturnType<typeof useCRUD<ProductCategory>>;
  countries: ReturnType<typeof useCRUD<Country>>;
  gekdCodes: ReturnType<typeof useCRUD<GekdCode>>;
  tnvedCodes: ReturnType<typeof useCRUD<TnvedCode>>;
  units: ReturnType<typeof useCRUD<Unit>>;
  bankAccounts: ReturnType<typeof useCRUD<BankAccountNew>>;
  contractors: ReturnType<typeof useCRUD<ContractorNew>>;
  contracts: ReturnType<typeof useCRUD<ContractNew>>;
  products: ReturnType<typeof useCRUD<ProductNew>>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const banks = useCRUD<Bank>('banks', [...MOCK_BANKS]);
  const warehouses = useCRUD<Warehouse>('warehouses', [...MOCK_WAREHOUSES]);
  const productCategories = useCRUD<ProductCategory>('productCategories', [...MOCK_PRODUCT_CATEGORIES]);

  // Статичные справочники (только для чтения)
  const countries = useCRUD<Country>('countries', [...MOCK_COUNTRIES]);
  const gekdCodes = useCRUD<GekdCode>('gekdCodes', [...MOCK_GEKD_CODES]);
  const tnvedCodes = useCRUD<TnvedCode>('tnvedCodes', [...MOCK_TNVED_CODES]);
  const units = useCRUD<Unit>('units', [...MOCK_UNITS]);

  const bankAccounts = useCRUD<BankAccountNew>('bankAccounts');
  const contractors = useCRUD<ContractorNew>('contractors', [...MOCK_CONTRACTORS]);
  const contracts = useCRUD<ContractNew>('contracts', [...MOCK_CONTRACTS]);
  const products = useCRUD<ProductNew>('products', [...MOCK_PRODUCTS]);

  const value: DataContextType = {
    banks,
    warehouses,
    productCategories,
    countries,
    gekdCodes,
    tnvedCodes,
    units,
    bankAccounts,
    contractors,
    contracts,
    products,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
